// DS: CR01F
import React, { useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import TextRenderer from '../../../TextRenderer';
import { DeviationLink } from '../../../Duperbrowse';
import OrderCommissionButton from '../../../ActionBar/buttons/OrderCommissionButton';
import { getCoverImageAttributes } from '../../Thumb/helpers';
import FeedCardLayout from '../_partials/FeedCardLayout';
import FeedCardTitle from '../_partials/FeedCardTitle';
import { FeedCardProps } from '../types';
import {
  DEFAULT_COVER_IMAGE_RESOLUTION_WIDTH,
  DEFAULT_COVER_IMAGE_RESOLUTION_HEIGHT,
} from '../constants';
import { MeasuredCookieType } from '@wix/da-hooks/pkg/useMeasureElement/redux/types';
import CommissionTiptapViewer from '@wix/da-editor-tiptap/pkg/components/Viewer/presets/Commission';

import legacyTheme from '../feedcard.legacy.theme.scss';
import expandableWrapperStyles from '../_partials/ExpandableWrapper/ExpandableWrapper.scss';
import s from './Commission.scss';

// commission is especially tall because of it's special cover image
const COLLAPSED_CONTENT_HEIGHT = 480;
const COVER_DOM_HEIGHT_DESKTOP = 280;
const COVER_DOM_HEIGHT_MOBILE = 295;

const CommissionFeedCardContents: React.FC<FeedCardProps> = ({
  className,
  deviation,
  extraActions,
  withWatchOrSubscribeButton,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const coverImage = getCoverImageAttributes({
    deviation,
    width: DEFAULT_COVER_IMAGE_RESOLUTION_WIDTH,
    height: DEFAULT_COVER_IMAGE_RESOLUTION_HEIGHT,
    allowHuge: true,
  });

  const coverImageDomHeight = isMobile ? COVER_DOM_HEIGHT_MOBILE : COVER_DOM_HEIGHT_DESKTOP;

  // maintain this so we can track when we can enable scroll restoration on duperbrowse
  const [isLayoutReady, setIsLayoutReady] = useState(false);
  const onLayoutReady = useCallback(() => {
    setIsLayoutReady(true);
  }, []);

  return (
    <FeedCardLayout
      className={classnames(s['root'], className)}
      deviation={deviation}
      extraActions={extraActions}
      isExpandable
      withCommentControl={false}
      withWatchOrSubscribeButton={withWatchOrSubscribeButton}
      collapsedContentHeight={COLLAPSED_CONTENT_HEIGHT}
      // special order button for special commission special case
      additionalActionbarActions={
        <div className={s['order-info']}>
          <span className={s['price']}>${deviation.commission?.dollarsCost}</span>
          <OrderCommissionButton deviation={deviation} />
        </div>
      }
      // special mobile expansion button to navigate to special commission on mobile :|
      expansionButton={
        isMobile ? (
          <DeviationLink
            className={classnames(s['toggle-button'], expandableWrapperStyles['toggle-button'])}
            deviation={deviation}
            disableScrollRestoration={!isLayoutReady}
          >
            <span className={expandableWrapperStyles['toggle-button-contents']}>
              {t('deviationview.feed.readmore')}
            </span>
          </DeviationLink>
        ) : null
      }
      onLayoutReady={onLayoutReady}
    >
      {coverImage && (
        <div className={s['cover-image']} style={{ height: coverImageDomHeight }}>
          <div className={s['blurred-bg-image']} {...coverImage} />
          <div className={s['fade']} />
          <div className={s['foreground-image']} {...coverImage} />
        </div>
      )}

      <div className={s['title-and-price']}>
        <FeedCardTitle
          className={s['title']}
          deviation={deviation}
          disableScrollRestoration={!isLayoutReady}
        />
        <span className={s['price']}>${deviation.commission?.dollarsCost}</span>
      </div>

      {deviation.textContent!.excerpt.trim().length > 0 && (
        <TextRenderer
          textContent={deviation.textContent}
          viewerKey={deviation.deviationId}
          legacyTheme="commission"
          className={classnames(s['content'], legacyTheme['legacy'], legacyTheme['override'])}
          tipTapViewer={CommissionTiptapViewer}
          cookieType={MeasuredCookieType.BROWSE_POST_FEED}
          defaultSize={{
            desktop: { width: 600, height: 600 },
            mobile: { width: 380, height: 400 },
          }}
        />
      )}
    </FeedCardLayout>
  );
};
CommissionFeedCardContents.displayName = 'CommissionFeedCardContents';
export default CommissionFeedCardContents;
